import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import Page from "components/page/Page";
import ScrollTabsLayout from "components/scrollTabsLayout/ScrollTabsLayout";
import ReportsSearchBar from "./ReportsSearchBar";
import ReportsPinnedBar from "./ReportsPinnedBar";
import ReportsCategorySection from "./ReportsCategorySection";
import ReportsListLoadingSkeleton from "./ReportsListLoadingSkeleton";
import ReportScheduleDrawer from "./ReportScheduleDrawer";
import { ReportLockedTooltipProvider } from "./ReportLockedTooltipContext";

import { StatusCode } from "api/protocols";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useReportsListSettings } from "hooks/useReportsListSettings/useReportsListSettings";
import { showError } from "redux/actions/ui";
import { searchHistoryUpdateSearch } from "redux/actions/searchBars/searchHistory";
import {
  applySearchToCategory,
  buildReportListCategories,
  categoryHasSearchMatches,
  flattenReports,
} from "pages/secure/shared/reports/buildReportListCategories";
import {
  IApiReportRow,
  IMergedReportEntry,
  IReportCategoryDefinition,
  TReportAdminScope,
} from "pages/secure/shared/reports/reportCatalog.types";
import { useQuickReportExport } from "hooks/useQuickReportExport/useQuickReportExport";
import { useNavigateToReportRun } from "hooks/useNavigateToReportRun/useNavigateToReportRun";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";

import { IPrimaryPageAction } from "components/page/PageActions";

import "./reportsList.scss";

const SEARCH_MIN_LENGTH = 2;

interface IReportsListPageProps {
  scope: TReportAdminScope;
  catalog: IReportCategoryDefinition[];
  settingsKeyPrefix: string;
  searchHistoryKey: string;
  loadReports: (token?: CancelToken) => Promise<{ status: number; data: IApiReportRow[] }>;
  errorMessage: string;
  primaryAction?: IPrimaryPageAction;
  dedicatedRouteCodes: ReadonlySet<string>;
}

export default function ReportsListPage(props: IReportsListPageProps) {
  const {
    scope,
    catalog,
    settingsKeyPrefix,
    searchHistoryKey,
    loadReports,
    errorMessage,
    primaryAction,
    dedicatedRouteCodes,
  } = props;

  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const permissions = useAppSelector(store => store.authStore.user?.permissions);
  const userClientId = useAppSelector(store => store.authStore.user?.client_id);

  const [apiReports, setApiReports] = useState<IApiReportRow[] | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Record<string, boolean>>({});
  const [scheduleReport, setScheduleReport] = useState<IMergedReportEntry | null>(null);
  const pendingScrollCategoryIdRef = useRef<string | null>(null);

  const { pinnedCodes, togglePin, isPinned } = useReportsListSettings(settingsKeyPrefix);
  const windowSize = useWindowSize();
  const isMobile = (windowSize?.width ?? MOBILE_WIDTH + 1) <= MOBILE_WIDTH;

  useEffect(() => {
    const source = axios.CancelToken.source();
    void (async () => {
      const response = await loadReports(source.token);

      if (source.token.reason) {
        return;
      }

      if (response.status !== Number(StatusCode.OK)) {
        dispatch(showError(errorMessage));
        setApiReports([]);
        return;
      }

      setApiReports(response.data);
    })();

    return () => source.cancel();
  }, [dispatch, errorMessage, loadReports]);

  const isLoading = apiReports === undefined;
  const effectiveSearchQuery = useMemo(() => {
    if (isLoading) {
      return "";
    }

    const trimmed = searchQuery.trim();
    if (trimmed.length > 0 && trimmed.length < SEARCH_MIN_LENGTH) {
      return "";
    }

    return searchQuery;
  }, [isLoading, searchQuery]);

  const reportCategories = useMemo(() => {
    if (apiReports === undefined) {
      return [];
    }

    return buildReportListCategories({
      apiReports,
      catalog,
      scope,
      permissions,
      dedicatedRouteCodes,
      userClientId,
    });
  }, [apiReports, catalog, dedicatedRouteCodes, permissions, scope, userClientId]);

  const isSearching = effectiveSearchQuery.trim().length > 0;

  const allVisibleReports = useMemo(() => flattenReports(reportCategories), [reportCategories]);

  const pinnedReports = useMemo(
    () =>
      pinnedCodes
        .map(code => allVisibleReports.find(report => report.code === code))
        .filter((report): report is IMergedReportEntry => report != null),
    [allVisibleReports, pinnedCodes],
  );

  useEffect(() => {
    if (reportCategories.length === 0) {
      return;
    }

    setExpandedCategoryIds(currentExpanded => {
      const nextExpanded = { ...currentExpanded };
      let changed = false;

      reportCategories.forEach(category => {
        if (nextExpanded[category.id] === undefined) {
          nextExpanded[category.id] = !isMobile;
          changed = true;
        }
      });

      return changed ? nextExpanded : currentExpanded;
    });
  }, [isMobile, reportCategories]);

  useEffect(() => {
    if (!isSearching) {
      return;
    }

    setExpandedCategoryIds(currentExpanded => {
      const nextExpanded = { ...currentExpanded };

      reportCategories.forEach(category => {
        if (categoryHasSearchMatches(category, effectiveSearchQuery)) {
          nextExpanded[category.id] = true;
        }
      });

      return nextExpanded;
    });
  }, [effectiveSearchQuery, isSearching, reportCategories]);

  const handleOpenReport = useCallback(
    (report: IMergedReportEntry) => {
      if (!report.hasPermission) {
        return;
      }

      history.push(report.url);
    },
    [history],
  );

  const handleToggleExpanded = useCallback((categoryId: string) => {
    setExpandedCategoryIds(currentExpanded => ({
      ...currentExpanded,
      [categoryId]: !currentExpanded[categoryId],
    }));
  }, []);

  const handleTabClick = useCallback(
    (categoryId: string) => {
      dispatch(searchHistoryUpdateSearch(searchHistoryKey, ""));
      setSearchQuery("");
      pendingScrollCategoryIdRef.current = categoryId;
      setExpandedCategoryIds(currentExpanded => ({
        ...currentExpanded,
        [categoryId]: true,
      }));
    },
    [dispatch, searchHistoryKey],
  );

  useLayoutEffect(() => {
    const categoryId = pendingScrollCategoryIdRef.current;
    if (!categoryId) {
      return;
    }

    pendingScrollCategoryIdRef.current = null;
    document.getElementById(categoryId)?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [expandedCategoryIds]);

  const canScheduleReports = scope === "facility";

  const { quickExport } = useQuickReportExport({ scope, dedicatedRouteCodes });
  const navigateToReportRun = useNavigateToReportRun({ scope, dedicatedRouteCodes });

  const handleScheduleReport = useCallback((report: IMergedReportEntry) => {
    setScheduleReport(report);
  }, []);

  const handleCloseScheduleDrawer = useCallback(() => {
    setScheduleReport(null);
  }, []);

  const scrollToolbar = (
    <div className="reports-list__toolbar">
      <ReportsSearchBar historyKey={searchHistoryKey} onSearch={setSearchQuery} />
      <ReportsPinnedBar pinnedReports={pinnedReports} onSelectReport={handleOpenReport} />
    </div>
  );

  const scrollTabSections = useMemo(
    () =>
      reportCategories.map(category => {
        const filteredCategory = applySearchToCategory(category, effectiveSearchQuery);
        const hasMatches = categoryHasSearchMatches(category, effectiveSearchQuery);

        return {
          id: category.id,
          label: t(category.title),
          badgeCount: isSearching
            ? hasMatches
              ? filteredCategory.reports.length
              : undefined
            : category.reports.length,
          showContent: hasMatches,
          content: (
            <ReportsCategorySection
              category={filteredCategory}
              embedded
              expanded={expandedCategoryIds[category.id] ?? !isMobile}
              onToggleExpanded={() => handleToggleExpanded(category.id)}
              onOpenReport={handleOpenReport}
              onTogglePin={togglePin}
              isPinned={isPinned}
              canSchedule={canScheduleReports}
              onScheduleReport={handleScheduleReport}
              onQuickExport={quickExport}
              onRunReport={navigateToReportRun}
            />
          ),
        };
      }),
    [
      reportCategories,
      effectiveSearchQuery,
      isSearching,
      t,
      expandedCategoryIds,
      isMobile,
      handleOpenReport,
      handleToggleExpanded,
      togglePin,
      isPinned,
      canScheduleReports,
      handleScheduleReport,
      quickExport,
      navigateToReportRun,
    ],
  );

  return (
    <Page
      className="reports-list-page"
      title={t("Reports")}
      subtitle={t("Pre-built reports for daily ops and period-end finance.")}
      primaryAction={primaryAction}
    >
      <div className="reports-list">
        <ReportLockedTooltipProvider>
          <div className="reports-list__scroll-tabs">
            {isLoading ? (
              <ReportsListLoadingSkeleton
                catalog={catalog}
                searchHistoryKey={searchHistoryKey}
                pinnedCount={pinnedCodes.length}
                onSearch={setSearchQuery}
              />
            ) : (
              <ScrollTabsLayout
                sections={scrollTabSections}
                size="md"
                fillAvailableHeight
                syncActiveTabOnScroll={false}
                wrapSectionsInCard={false}
                suppressActiveTab={isSearching}
                onTabClick={handleTabClick}
                contentHeader={scrollToolbar}
                emptyState={{
                  title: t("No Reports Found"),
                  content: t("Please adjust your search and try again"),
                }}
              />
            )}
          </div>
        </ReportLockedTooltipProvider>
      </div>
      {canScheduleReports ? (
        <ReportScheduleDrawer
          open={scheduleReport != null}
          report={scheduleReport}
          onClose={handleCloseScheduleDrawer}
        />
      ) : null}
    </Page>
  );
}
